import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend } from 'chart.js';
import { useAuth } from '../../context/AuthContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const Postura = () => {
  const { user } = useAuth();
  const [posturas, setPosturas] = useState([]);

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        const res = await axios.get(`/api/users/signos/usuario/${user.id}`);
        setPosturas(res.data.reverse());
      } catch (err) {
        console.error('Error al obtener datos de postura:', err);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [user]);

  // Últimos 50 datos
  const ultimos = posturas.slice(-50);
  const labels = ultimos.map((_, i) => `#${i + 1}`);

  const posturaValues = ultimos.map(p => (p.postura === 'mala' ? 1 : 0)); // 1=mala, 0=buena

  const barData = {
    labels,
    datasets: [
      {
        label: 'Mala Postura',
        data: posturaValues,
        backgroundColor: posturaValues.map(v => (v === 1 ? 'rgba(239, 68, 68, 0.7)' : 'rgba(74, 222, 128, 0.7)')),
        borderWidth: 1,
      },
    ],
  };

  const conteo = ultimos.reduce(
    (acc, curr) => {
      if (curr.postura === 'mala') acc.mala++;
      else acc.buena++;
      return acc;
    },
    { buena: 0, mala: 0 }
  );

  const pieData = {
    labels: ['Buena Postura', 'Mala Postura'],
    datasets: [
      {
        data: [conteo.buena, conteo.mala],
        backgroundColor: ['#4ade80', '#ef4444'], // verde y rojo
        hoverOffset: 4,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          callback: value => (value === 1 ? 'Mala' : 'Buena'),
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: context => (context.raw === 1 ? 'Mala Postura' : 'Buena Postura'),
        },
      },
    },
  };

  return (
    <div className="w-full p-6 space-y-8 max-w-5xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">Monitoreo de Postura Corporal</h1>

      <div className="w-full backdrop-blur-md bg-white/30 shadow-md rounded-xl p-6 flex flex-col lg:flex-row lg:items-center gap-6">
        <div className="lg:w-1/2 text-black space-y-4">
          <h2 className="text-xl font-bold text-indigo-700">Historial de Postura</h2>
          <p>
            Este gráfico muestra un registro de las últimas 50 mediciones de postura corporal, clasificando si la postura fue buena o mala en cada punto de datos.
            Mantener una buena postura es esencial para prevenir dolores musculares, problemas de columna y mejorar la respiración y circulación.
          </p>
          <p>
            La mala postura prolongada puede ocasionar fatiga muscular, tensión en articulaciones y afectar la calidad de vida. Es importante monitorear y corregir estos hábitos
            para mantener un bienestar óptimo.
          </p>
        </div>

        <div className="lg:w-1/2 flex justify-center">
          <div className="bg-white/80 p-4 rounded-xl shadow-md max-w-md w-full self-center">
            <Bar data={barData} options={barOptions} />
          </div>
        </div>
      </div>

      <div className="w-full backdrop-blur-md bg-white/30 shadow-md rounded-xl p-6 flex flex-col lg:flex-row lg:items-center gap-6">
        <div className="lg:w-1/2 text-black space-y-4 order-2 lg:order-1">
          <h2 className="text-xl font-bold text-indigo-700">Distribución de Posturas</h2>
          <p>
            Aquí puedes observar el porcentaje total de posturas buenas y malas en el último conjunto de datos. Un mayor porcentaje de buena postura indica hábitos saludables,
            mientras que un porcentaje elevado de mala postura sugiere la necesidad de intervención para corregirla.
          </p>
          <p>
            Este análisis es útil para evaluar tendencias y motivar el cambio de comportamiento mediante el monitoreo constante y la retroalimentación visual.
          </p>
        </div>

        <div className="lg:w-1/2 flex justify-center order-1 lg:order-2">
          <div className="bg-white/80 p-4 rounded-xl shadow-md max-w-md w-full self-center">
            <Pie data={pieData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Postura;
